import { JSX, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { interviewActions } from 'src/redux/slices/Interview/interviewSlice';
import {
  useAnswerSubmit,
  useIPE,
  useValidateVisibility,
} from 'src/redux/slices/Interview/hooks';
import { computeVisibleQuestionQueue } from 'src/redux/slices/Interview/questionQueue';
import {
  makeStepKey,
  makeGroupInstanceKey,
} from 'src/redux/slices/Interview/stepKey';
import type {
  ExistingSurveyFile,
  HistoryStep,
  InterviewState,
  NextGroup,
  QuestionItem,
} from 'src/redux/slices/Interview/types';
import { evaluateVisibility } from 'src/redux/slices/Interview/questionQueue';
import { validateFormat } from '../validation';
import SectionLabel from '../components/SectionLabel';
import QuestionText from '../components/QuestionText';
import HelpText from '../components/HelpText';
import FileUploadNotice from '../components/FileUploadNotice';
import AIProvenanceHint from '../components/AIProvenanceHint';
import ValidationError from '../components/ValidationError';
import InstanceBadge from '../components/InstanceBadge';
import QuestionInput from '../components/QuestionInput';
import InterviewFooter from '../components/InterviewFooter';
import ContentCard from '../components/ContentCard';
import { fadeStyle } from 'src/components/NewRenterInterface/constants';
import {
  isSameInstance,
  isSameGroup,
  buildInstanceAnswersMap,
  deepEqual,
  buildAnswerPayload,
} from './reviewNav';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const noop = (..._args: any[]): void => {
  // intentionally empty — used for non-applicable callbacks in reviewing mode
};

const _only = (a: number[] | null | undefined): string =>
  a == null || a.length === 0
    ? '-'
    : `[${a.length} ${a.slice(0, 8).join(',')}${a.length > 8 ? '...' : ''}]`;

/**
 * Walk forward through history from cursor+1, dropping invisible steps
 * and confirming the first visible one. Returns true when we exhaust all
 * remaining steps without finding a visible one — meaning the caller
 * should resync from the server (callIPE).
 *
 * Uses a working copy of history because dispatch(dropStep) mutates the
 * Redux array but the useSelector closure stays stale across iterations.
 */
async function walkForwardGate(
  history: HistoryStep[],
  cursor: number,
  effectiveVerified: number,
  editedStep: HistoryStep,
  nextAnswersByKey: Record<string, unknown>,
  groupsById: Record<string, NextGroup>,
  validate: (qitIds: number[]) => Promise<Record<number, boolean>>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: (...args: any[]) => any
): Promise<boolean> {
  const workingHistory = [...history];
  const idx = cursor + 1;

  while (idx < workingHistory.length) {
    if (idx <= effectiveVerified) {
      dispatch(interviewActions.confirmStep(idx));
      dispatch(interviewActions.moveCursorForward());
      return false;
    }

    const nextStep = workingHistory[idx];

    if (!nextStep || nextStep.kind !== 'question' || nextStep.qitId == null) {
      dispatch(interviewActions.confirmStep(idx));
      dispatch(interviewActions.moveCursorForward());
      return false;
    }

    let visible: boolean;

    if (isSameInstance(nextStep, editedStep)) {
      // Same group instance — evaluate visibility locally using the updated answers
      const nextGroup =
        nextStep.groupId != null
          ? groupsById[
              makeGroupInstanceKey(
                nextStep.groupId,
                nextStep.answerGroupId,
                nextStep.instanceSeq
              )
            ]
          : null;
      const nextQuestion = nextGroup?.questions?.find(
        (q: QuestionItem) => q.qit_id === nextStep.qitId
      );
      if (!nextQuestion || !nextGroup) {
        dispatch(interviewActions.dropStep(idx));
        workingHistory.splice(idx, 1);
        continue;
      }
      const answersMap = buildInstanceAnswersMap(
        history,
        nextAnswersByKey,
        nextStep.groupId,
        nextStep.answerGroupId,
        nextStep.instanceSeq
      );
      visible = evaluateVisibility(
        nextQuestion,
        answersMap,
        nextGroup.questions
      );
    } else if (isSameGroup(nextStep, editedStep)) {
      // Same group, different instance — always confirm (different answer set)
      dispatch(interviewActions.confirmStep(idx));
      dispatch(interviewActions.moveCursorForward());
      return false;
    } else {
      // Different group entirely — ask the server whether this QIT is still visible
      const results = await validate([nextStep.qitId]);
      visible = results[nextStep.qitId] ?? false;
    }

    if (visible) {
      dispatch(interviewActions.confirmStep(idx));
      dispatch(interviewActions.moveCursorForward());
      return false;
    }

    // Not visible — drop the step and keep looking (don't advance idx;
    // splice shifted the next element into this position)
    dispatch(interviewActions.dropStep(idx));
    workingHistory.splice(idx, 1);
  }

  return true;
}

export default function QuestionScreen(): JSX.Element {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    currentGroup,
    visibleQuestionQueue,
    currentQuestionIndex,
    answerInFlight,
    answerError,
    loading,
    surveyId,
    certId,
    currentAnswerGroupId,
    localAnswers,
    deferred,
    onlyQitIds,
    backPath,
    history,
    cursor,
    verifiedIndex,
    groupsById,
    answersByKey,
    frontierResyncNeeded,
    instanceSeqByGroup,
    answerGroupQits,
    activeAgTarget,
    pendingFileQits,
  } = useSelector((state: { interview: InterviewState }) => state.interview);

  const instanceKey = makeGroupInstanceKey(
    currentGroup?.group_id ?? null,
    currentAnswerGroupId ?? null,
    currentGroup?.group_id != null
      ? instanceSeqByGroup[currentGroup.group_id] ?? 0
      : 0
  );
  const instanceDeferredQitIds: number[] = deferred.qitIds[instanceKey] || [];
  const flatDeferredQitIds: number[] = [
    ...new Set(Object.values(deferred.qitIds).flat()),
  ];

  const { submitAnswer } = useAnswerSubmit();
  const { callIPE } = useIPE();
  const { validate } = useValidateVisibility();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [localValue, setLocalValue] = useState<any>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [fileReadyState, setFileReadyState] = useState({
    processing: false,
    ready: false,
  });
  const continueTriggerRef = useRef<(() => void) | null>(null);
  const handleContinueTrigger = useCallback((fn: (() => void) | null) => {
    continueTriggerRef.current = fn;
  }, []);

  const advanceToNextAnswerGroup = useCallback(
    (agQits?: Record<number, number[]> | null, scoped = false): boolean => {
      const effective = agQits !== undefined ? agQits : answerGroupQits;
      if (!effective || Object.keys(effective).length === 0) {
        return false;
      }
      const nextAgId = Number(Object.keys(effective)[0]);
      const nextQitIds = effective[nextAgId];
      if (!nextQitIds || nextQitIds.length === 0) {
        return false;
      }
      if (scoped) {
        dispatch(
          interviewActions.advanceToNextAnswerGroup({
            nextAgId,
            nextQitIds,
          })
        );
      } else {
        dispatch(
          interviewActions.activateAnswerGroup({
            nextAgId,
            nextQitIds,
          })
        );
      }
      callIPE({
        onlyQitIds: scoped ? nextQitIds : undefined,
        answerGroupQits: effective,
      });
      return true;
    },
    [answerGroupQits, dispatch, callIPE]
  );

  /**
   * Compute what answerGroupQits looks like after removing a QIT from the
   * active answer_group. Used in handleSaveAndNext / handleFileComplete to
   * avoid sending stale answerGroupQits to the IPE (closure captures pre-
   * dispatch value, but recordLocalAnswer already mutated Redux).
   */
  const computeNextAnswerGroupQits = useCallback(
    (qitId: number): Record<number, number[]> | null => {
      if (!answerGroupQits || activeAgTarget == null) {
        return answerGroupQits ?? null;
      }
      const next: Record<number, number[]> = {};
      let hasAny = false;
      for (const agId of Object.keys(answerGroupQits)) {
        const id = Number(agId);
        const filtered =
          id === activeAgTarget
            ? answerGroupQits[id].filter((q) => q !== qitId)
            : answerGroupQits[id];
        if (filtered.length > 0) {
          next[id] = filtered;
          hasAny = true;
        }
      }
      return hasAny ? next : null;
    },
    [answerGroupQits, activeAgTarget]
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (value: any): void => {
    setLocalValue(value);
    if (validationError) {
      setValidationError(null);
    }
  };

  const handleAnswerGroupResolved = useCallback(
    (id: number): void => {
      dispatch(interviewActions.setAnswerGroupId(id));
    },
    [dispatch]
  );

  // Ref to hold the latest handleSaveAndNext for the keydown listener
  const saveAndNextRef = useRef<(() => Promise<void>) | undefined>(undefined);

  // Ref to hold the latest reviewing save handler for the keydown listener
  const reviewingSaveRef = useRef<(() => Promise<void>) | undefined>(undefined);

  // Gate to prevent concurrent forward-gate runs
  const gateInFlightRef = useRef(false);

  // Ref that tracks isReviewing so the stable handleKeyDown callback can guard without
  // re-registering the document listener on every cursor move.
  const isReviewingRef = useRef(false);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent): void => {
      if (e.key !== 'Enter') {
        return;
      }
      if (answerInFlight || loading) {
        return;
      }
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'textarea') {
        return;
      }
      if ((e.target as HTMLElement)?.isContentEditable) {
        return;
      }
      e.preventDefault();
      if (isReviewingRef.current) {
        reviewingSaveRef.current?.();
      } else {
        saveAndNextRef.current?.();
      }
    },
    [answerInFlight, loading]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleBack = useCallback(() => {
    dispatch(interviewActions.moveCursorBack());
  }, [dispatch]);

  const isReviewing = history.length > 0 && cursor < history.length;
  isReviewingRef.current = isReviewing;

  useEffect(() => {
    if (isReviewing) {
      setLocalValue(null);
      setValidationError(null);
      // NOTE: do NOT reset fileReadyState here. React runs child effects
      // before parent effects, so InterviewDocHub's children (FileUploadInput)
      // report onReadyStateChange during mount — BEFORE this effect runs.
      // Resetting here wipes those values and they never re-fire (hasFiles
      // doesn't change on subsequent uploads). Fresh state per step is
      // already guaranteed by key={qit_id} on InterviewDocHub, which remounts
      // and re-reports on every step change.
    }
  }, [cursor, isReviewing]);

  // ── reviewing save & forward gate ─────────────────────────────────────

  // For file questions in reviewing mode: store the new file list and advance.
  // Does not call callIPE — file uploads don't affect conditional visibility of
  // other questions, so a simple cursor advance is sufficient.
  const handleReviewingFileComplete = useCallback(
    (fileList?: ExistingSurveyFile[]): void => {
      const step = history[cursor];
      if (step?.kind === 'question' && step.qitId != null) {
        const key = makeStepKey(step);
        const value =
          fileList && fileList.length > 0 ? fileList : [{ status: 'success' }];
        dispatch(interviewActions.setDurableAnswer({ key, value }));
        dispatch(interviewActions.markStepAnswered(key));
      }
      dispatch(interviewActions.moveCursorForward());
      setFileReadyState({ processing: false, ready: false });
    },
    [history, cursor, dispatch]
  );

  const handleReviewingSaveAndNext = async (): Promise<void> => {
    if (gateInFlightRef.current) {
      return;
    }
    gateInFlightRef.current = true;

    try {
      const step = history[cursor];
      if (!step || step.kind !== 'question' || step.qitId == null) {
        gateInFlightRef.current = false;
        return;
      }

      const reviewedGroup =
        step.groupId != null
          ? groupsById[
              makeGroupInstanceKey(
                step.groupId,
                step.answerGroupId,
                step.instanceSeq
              )
            ]
          : null;
      const reviewedQuestion: QuestionItem | undefined =
        reviewedGroup?.questions?.find(
          (q: QuestionItem) => q.qit_id === step.qitId
        );
      if (!reviewedQuestion || !reviewedGroup) {
        gateInFlightRef.current = false;
        return;
      }

      const key = makeStepKey(step);
      const stored =
        key in answersByKey
          ? answersByKey[key]
          : reviewedQuestion.existing_answer;
      const current = localValue !== null ? localValue : stored ?? null;
      const nextAnswersByKey = { ...answersByKey, [key]: current };

      let didEdit = false;

      if (!deepEqual(current, stored)) {
        didEdit = true;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let effectiveValue: any = current;
        if (
          ['bool', 'choices', 'frequency'].includes(
            reviewedQuestion.question_type
          ) &&
          Array.isArray(effectiveValue)
        ) {
          effectiveValue = effectiveValue[0] ?? null;
        }

        const payload = buildAnswerPayload(
          surveyId as number,
          step.qitId,
          reviewedQuestion.question_type,
          step.answerGroupId,
          effectiveValue
        );
        const success = await submitAnswer(payload, {
          syncCurrentAnswerGroup: false,
        });
        if (!success) {
          setValidationError(answerError ?? 'Failed to save answer.');
          gateInFlightRef.current = false;
          return;
        }

        dispatch(
          interviewActions.setDurableAnswer({ key, value: effectiveValue })
        );
        dispatch(interviewActions.invalidateFrom(cursor));
        dispatch(interviewActions.markStepAnswered(key));
        dispatch(interviewActions.setFrontierResyncNeeded(true));
      }

      const reachedEnd = await walkForwardGate(
        history,
        cursor,
        didEdit ? cursor : verifiedIndex,
        step,
        nextAnswersByKey,
        groupsById,
        validate,
        dispatch
      );

      if (reachedEnd) {
        if (didEdit || frontierResyncNeeded) {
          dispatch(interviewActions.moveCursorForward());
          setLocalValue(null);
          setValidationError(null);
          dispatch(interviewActions.setFrontierResyncNeeded(false));
          dispatch(interviewActions.setWorkItemType('loading'));
          dispatch(interviewActions.setLoading(true));
          callIPE({ skippedQitIds: flatDeferredQitIds });
        } else {
          dispatch(interviewActions.moveCursorForward());
        }
      }
    } finally {
      setLocalValue(null);
      gateInFlightRef.current = false;
    }
  };

  reviewingSaveRef.current = handleReviewingSaveAndNext;

  /**
   * Reviewing-mode "Skip for now": re-defer the currently reviewed step (if it
   * was answered) and advance the cursor. The question is tracked as skipped
   * but its saved answer is preserved — the backend keeps a persisted
   * SurveyAnswer on skip. Keeps the skip affordance available after navigating
   * back to a skipped question, so the renter isn't stranded in reviewing mode.
   */
  const handleReviewingSkip = useCallback((): void => {
    const step = history[cursor];
    if (step?.kind === 'question' && step.qitId != null) {
      dispatch(interviewActions.markStepSkipped(makeStepKey(step)));
    }
    // Discard any unsaved in-progress edit. A saved answer in answersByKey is
    // intentionally kept (the backend keeps a persisted SurveyAnswer on skip),
    // but a value the renter typed without saving must not leak forward.
    setLocalValue(null);
    setValidationError(null);
    dispatch(interviewActions.moveCursorForward());
  }, [history, cursor, dispatch]);

  // NOTE: currentQitId is still resolved from state even when reviewing,
  // so this useEffect runs unconditionally (hooks must never be conditional).
  const currentQitId = visibleQuestionQueue[currentQuestionIndex];
  const question: QuestionItem | undefined = currentGroup?.questions?.find(
    (q: QuestionItem) => q.qit_id === currentQitId
  );

  const currentQuestionIsFile = question?.question_type === 'file';

  useEffect(() => {
    // Neutral reset on question change. The doc-hub (FileUploadInput) owns the
    // ready/processing state for a file question and reports it on mount, so
    // resetting here would wipe that first report (parent effects run after
    // child effects) and leave Continue hidden on a question that already has
    // files (ENG-4064). InterviewDocHub remounts per qit via key={qit_id}, so
    // its report is always fresh.
    if (!currentQuestionIsFile) {
      setFileReadyState({ processing: false, ready: false });
    }
    setLocalValue(null);
  }, [currentQitId, currentAnswerGroupId, currentQuestionIsFile]);

  // ── reviewing mode ───────────────────
  if (isReviewing) {
    const step = history[cursor];
    if (step.kind !== 'question') {
      return <Box data-testid="question-screen" />;
    }

    const reviewedGroup =
      step.groupId != null
        ? groupsById[
            makeGroupInstanceKey(
              step.groupId,
              step.answerGroupId,
              step.instanceSeq
            )
          ]
        : null;
    const reviewedQuestion: QuestionItem | undefined =
      reviewedGroup?.questions?.find(
        (q: QuestionItem) => q.qit_id === step.qitId
      );

    if (!reviewedQuestion || !reviewedGroup) {
      return <Box data-testid="question-screen" />;
    }

    const stepKey = makeStepKey(step);
    const storedValue =
      stepKey in answersByKey
        ? answersByKey[stepKey]
        : reviewedQuestion.existing_answer;

    const isFileQuestion = reviewedQuestion.question_type === 'file';

    return (
      <Box
        data-testid="question-screen"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          id="interview-body"
          sx={{
            flex: { xs: 1, md: '0 0 auto' },
            overflow: 'auto',
            bgcolor: { md: 'background.neutral' },
            display: 'flex',
            alignItems: { md: 'flex-start' },
            justifyContent: { md: 'center' },
            pt: { md: 4 },
            gap: { md: 3 },
          }}
        >
          <ContentCard key={stepKey} sx={fadeStyle}>
            <Box>
              <SectionLabel label={reviewedGroup.section_name || ''} />
              {reviewedGroup.group_name &&
                reviewedGroup.group_name !== reviewedGroup.section_name && (
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary' }}
                  >
                    {reviewedGroup.group_name}
                  </Typography>
                )}
            </Box>
            <Box>
              <InstanceBadge label={reviewedGroup?.instance_label ?? null} />
              <QuestionText
                text={reviewedQuestion.question_text}
                required={reviewedQuestion.necessity === 1}
              />
              <HelpText text={reviewedQuestion.help_text} />
              <FileUploadNotice
                show={
                  reviewedQuestion.options?.some(
                    (o) => o.requires_file_upload
                  ) ?? false
                }
              />
            </Box>
            <AIProvenanceHint
              show={
                reviewedQuestion.always_ask && reviewedQuestion.ai_extracted
              }
            />

            <ValidationError message={validationError ?? answerError} />
            <QuestionInput
              question={reviewedQuestion}
              value={localValue ?? storedValue}
              onChange={isFileQuestion ? noop : handleChange}
              surveyId={surveyId as number}
              answerGroupId={step.answerGroupId ?? null}
              questionGroupId={reviewedGroup.group_id}
              onAnswerGroupResolved={noop}
              certificationId={certId ?? undefined}
              onComplete={isFileQuestion ? handleReviewingFileComplete : noop}
              onReadyStateChange={isFileQuestion ? setFileReadyState : noop}
              onContinueTrigger={isFileQuestion ? handleContinueTrigger : noop}
              instanceKey={`s${step.instanceSeq}`}
              skipInitialFileFetch={
                isFileQuestion &&
                reviewedGroup.group_type === 'multi' &&
                step.answerGroupId == null
              }
              existingSurveyFiles={
                isFileQuestion && Array.isArray(storedValue)
                  ? (storedValue as ExistingSurveyFile[])
                  : undefined
              }
            />
            {/* Desktop inline: Back + Continue (file) or Save & Next (non-file) */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                justifyContent: 'flex-end',
                gap: 2,
              }}
            >
              {cursor > 0 && (
                <Button
                  onClick={handleBack}
                  disabled={answerInFlight || loading}
                  data-testid="back-button-inline"
                  sx={{
                    height: 44,
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    color: 'text.secondary',
                    fontSize: 14,
                    fontWeight: 500,
                    px: 2.5,
                    textTransform: 'none',
                  }}
                >
                  Back
                </Button>
              )}
              <Button
                onClick={handleReviewingSkip}
                disabled={answerInFlight || loading}
                data-testid="skip-button-reviewing-inline"
                sx={{
                  height: 44,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  color: 'text.secondary',
                  fontSize: 14,
                  fontWeight: 500,
                  px: 2.5,
                  textTransform: 'none',
                }}
              >
                Skip for now
              </Button>
              {isFileQuestion ? (
                fileReadyState.ready ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => continueTriggerRef.current?.()}
                    data-testid="file-continue-button-reviewing-inline"
                    sx={{
                      height: 44,
                      borderRadius: '12px',
                      fontSize: 16,
                      fontWeight: 600,
                      px: 3,
                      textTransform: 'none',
                    }}
                  >
                    <span>Continue</span>
                  </Button>
                ) : fileReadyState.processing ? (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled
                    data-testid="file-processing-button-reviewing-inline"
                    startIcon={<CircularProgress size={16} color="inherit" />}
                    sx={{
                      height: 44,
                      borderRadius: '12px',
                      fontSize: 16,
                      fontWeight: 600,
                      px: 3,
                      textTransform: 'none',
                    }}
                  >
                    <span>Processing files...</span>
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleReviewingSaveAndNext}
                    disabled={answerInFlight || loading}
                    data-testid="next-question-reviewing-button-inline"
                    sx={{
                      height: 44,
                      borderRadius: '12px',
                      fontSize: 16,
                      fontWeight: 600,
                      px: 3,
                      textTransform: 'none',
                    }}
                  >
                    <span>Next Question</span>
                  </Button>
                )
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleReviewingSaveAndNext}
                  disabled={answerInFlight || loading}
                  data-testid="save-next-reviewing-button"
                  startIcon={
                    answerInFlight ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                  sx={{
                    height: 44,
                    borderRadius: '12px',
                    fontSize: 16,
                    fontWeight: 600,
                    px: 3,
                    textTransform: 'none',
                  }}
                >
                  <span>Save &amp; Next Question</span>
                </Button>
              )}
            </Box>
          </ContentCard>
        </Box>
        {/* Mobile footer: file questions get Continue/Processing/Next; others get Save & Next */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {isFileQuestion ? (
            fileReadyState.ready ? (
              <InterviewFooter
                primaryLabel="Continue"
                onPrimary={() => continueTriggerRef.current?.()}
                primaryTestId="file-continue-button-reviewing"
                secondaryLabel="Skip for now"
                onSecondary={handleReviewingSkip}
                secondaryDisabled={answerInFlight || loading}
                secondaryTestId="skip-button"
                tertiaryLabel={cursor > 0 ? 'Back' : undefined}
                onTertiary={cursor > 0 ? handleBack : undefined}
                tertiaryDisabled={answerInFlight || loading}
                tertiaryTestId="back-button"
              />
            ) : fileReadyState.processing ? (
              <InterviewFooter
                primaryLabel="Processing files..."
                primaryDisabled
                onPrimary={noop}
                primaryStartIcon={
                  <CircularProgress size={16} color="inherit" />
                }
                primaryTestId="file-processing-button-reviewing"
                tertiaryLabel={cursor > 0 ? 'Back' : undefined}
                onTertiary={cursor > 0 ? handleBack : undefined}
                tertiaryDisabled={answerInFlight || loading}
                tertiaryTestId="back-button"
              />
            ) : (
              <InterviewFooter
                primaryLabel="Next Question"
                onPrimary={handleReviewingSaveAndNext}
                primaryDisabled={answerInFlight || loading}
                primaryTestId="next-question-reviewing-button"
                secondaryLabel="Skip for now"
                onSecondary={handleReviewingSkip}
                secondaryDisabled={answerInFlight || loading}
                secondaryTestId="skip-button"
                tertiaryLabel={cursor > 0 ? 'Back' : undefined}
                onTertiary={cursor > 0 ? handleBack : undefined}
                tertiaryDisabled={answerInFlight || loading}
                tertiaryTestId="back-button"
              />
            )
          ) : cursor > 0 ? (
            <InterviewFooter
              primaryLabel="Save & Next Question"
              onPrimary={handleReviewingSaveAndNext}
              primaryDisabled={answerInFlight || loading}
              primaryTestId="save-next-reviewing-button-mobile"
              primaryStartIcon={
                answerInFlight ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              secondaryLabel="Skip for now"
              onSecondary={handleReviewingSkip}
              secondaryDisabled={answerInFlight || loading}
              secondaryTestId="skip-button"
              tertiaryLabel="Back"
              onTertiary={handleBack}
              tertiaryDisabled={answerInFlight || loading}
              tertiaryTestId="back-button"
            />
          ) : (
            <InterviewFooter
              primaryLabel="Save & Next Question"
              onPrimary={handleReviewingSaveAndNext}
              primaryDisabled={answerInFlight || loading}
              primaryTestId="save-next-reviewing-button-mobile"
              primaryStartIcon={
                answerInFlight ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              secondaryLabel="Skip for now"
              onSecondary={handleReviewingSkip}
              secondaryDisabled={answerInFlight || loading}
              secondaryTestId="skip-button"
            />
          )}
        </Box>
      </Box>
    );
  }

  // ── frontier mode ───────────────────

  if (!question || !currentGroup) {
    return <Box data-testid="question-screen" />;
  }

  const handleSaveAndNext = async (): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let effectiveValue: any =
      localValue !== null ? localValue : question.existing_answer ?? null;
    if (
      ['bool', 'choices', 'frequency'].includes(question.question_type) &&
      Array.isArray(effectiveValue)
    ) {
      effectiveValue = effectiveValue[0] ?? null;
    }

    if (
      question.necessity === 1 &&
      (effectiveValue === null ||
        effectiveValue === '' ||
        (Array.isArray(effectiveValue) && effectiveValue.length === 0))
    ) {
      setValidationError('This field is required.');
      return;
    }

    const formatError = validateFormat(question.question_type, effectiveValue);
    if (formatError) {
      setValidationError(formatError);
      return;
    }
    setValidationError(null);

    if (question.question_type === 'file') {
      return;
    } else {
      const isEmpty =
        effectiveValue === null ||
        effectiveValue === '' ||
        (Array.isArray(effectiveValue) && effectiveValue.length === 0);

      if (!isEmpty) {
        const payload = buildAnswerPayload(
          surveyId as number,
          question.qit_id,
          question.question_type,
          currentAnswerGroupId,
          effectiveValue
        );

        const success = await submitAnswer(payload);
        if (!success) {
          return;
        }
      }
    }

    dispatch(
      interviewActions.recordLocalAnswer({
        qitId: question.qit_id,
        value: effectiveValue,
      })
    );

    const updatedLocalAnswers = {
      ...localAnswers,
      [question.qit_id]: effectiveValue,
    };
    const remaining = computeVisibleQuestionQueue(
      currentGroup.questions,
      updatedLocalAnswers,
      instanceDeferredQitIds,
      pendingFileQits.map((p) => p.qit_id),
      onlyQitIds
    );

    if (remaining.length > 0) {
      dispatch(interviewActions.updateVisibleQueue(remaining));
    } else {
      const nextOnlyQitIds = onlyQitIds
        ? onlyQitIds.filter((id) => !(id in updatedLocalAnswers))
        : undefined;
      let nextAgMap = answerGroupQits;
      if (answerGroupQits && activeAgTarget != null) {
        nextAgMap = { ...answerGroupQits };
        delete nextAgMap[activeAgTarget];
      }

      if (process.env.NODE_ENV !== 'production') {
        console.log(
          `[EXH] group=${currentGroup?.group_id}:${currentGroup?.group_type}  ` +
            `only=${_only(onlyQitIds)}  nextOnly=${_only(nextOnlyQitIds)}  ` +
            `agKeys=${
              answerGroupQits
                ? Object.keys(answerGroupQits).join(',') || '{}'
                : '-'
            }  ` +
            `activeAg=${activeAgTarget ?? '-'}`
        );
      }
      if (
        currentGroup?.group_type === 'multi' &&
        advanceToNextAnswerGroup(nextAgMap)
      ) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[EXH] → ADVANCE_AG');
        }
        return;
      }
      if (onlyQitIds && !nextOnlyQitIds?.length) {
        if (answerGroupQits && Object.keys(answerGroupQits).length === 0) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('[EXH] → ADD_ANOTHER (scoped, empty agQits)');
          }
          dispatch(interviewActions.clearAnswerGroupState());
          dispatch(interviewActions.setWorkItemType('add_another'));
          return;
        }
        if (process.env.NODE_ENV !== 'production') {
          console.log(`[EXH] → NAVIGATE back=${backPath ?? '/renter'}`);
        }
        navigate(backPath ?? '/renter');
        return;
      }
      if (currentGroup?.group_type === 'multi' && !onlyQitIds) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[EXH] → ADD_ANOTHER');
        }
        if (answerGroupQits) {
          dispatch(interviewActions.clearAnswerGroupState());
        }
        dispatch(interviewActions.setWorkItemType('add_another'));
      } else {
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            `[EXH] → CALL_IPE  nextOnly=${_only(
              nextOnlyQitIds ?? undefined
            )}  nextAgQits=${_only(
              computeNextAnswerGroupQits(question.qit_id)
                ? Object.values(
                    computeNextAnswerGroupQits(question.qit_id) ?? {}
                  ).flat()
                : null
            )}`
          );
        }
        dispatch(interviewActions.setLoading(true));
        callIPE({
          skippedQitIds: flatDeferredQitIds,
          onlyQitIds: nextOnlyQitIds,
          answerGroupQits: computeNextAnswerGroupQits(question.qit_id),
        });
      }
    }

    setLocalValue(null);
  };

  saveAndNextRef.current = handleSaveAndNext;

  const handleFileComplete = (fileList?: ExistingSurveyFile[]): void => {
    const storedValue =
      fileList && fileList.length > 0 ? fileList : [{ status: 'success' }];
    dispatch(
      interviewActions.recordLocalAnswer({
        qitId: question.qit_id,
        value: storedValue,
      })
    );

    const updatedLocalAnswers = {
      ...localAnswers,
      [question.qit_id]: storedValue,
    };
    const remaining = computeVisibleQuestionQueue(
      currentGroup.questions,
      updatedLocalAnswers,
      instanceDeferredQitIds,
      pendingFileQits.map((p) => p.qit_id),
      onlyQitIds
    );

    if (remaining.length > 0) {
      dispatch(interviewActions.updateVisibleQueue(remaining));
    } else {
      const nextOnlyQitIds = onlyQitIds
        ? onlyQitIds.filter((id) => !(id in updatedLocalAnswers))
        : undefined;
      let nextAgMap2 = answerGroupQits;
      if (answerGroupQits && activeAgTarget != null) {
        nextAgMap2 = { ...answerGroupQits };
        delete nextAgMap2[activeAgTarget];
      }
      if (
        currentGroup?.group_type === 'multi' &&
        advanceToNextAnswerGroup(nextAgMap2)
      ) {
        return;
      }
      // Same guard as handleSaveAndNext: scoped session takes priority over
      // add_another so multi-group file management returns to the caller.
      if (onlyQitIds && !nextOnlyQitIds?.length) {
        if (answerGroupQits && Object.keys(answerGroupQits).length === 0) {
          dispatch(interviewActions.clearAnswerGroupState());
          dispatch(interviewActions.setWorkItemType('add_another'));
          return;
        }
        navigate(backPath ?? '/renter');
        return;
      }
      if (currentGroup.group_type === 'multi' && !onlyQitIds) {
        if (answerGroupQits) {
          dispatch(interviewActions.clearAnswerGroupState());
        }
        dispatch(interviewActions.setWorkItemType('add_another'));
      } else {
        dispatch(interviewActions.setLoading(true));
        callIPE({
          skippedQitIds: flatDeferredQitIds,
          onlyQitIds: nextOnlyQitIds,
          answerGroupQits: computeNextAnswerGroupQits(question.qit_id),
        });
      }
    }
  };

  const handleSkip = (): void => {
    dispatch(interviewActions.skipQuestion(question.qit_id));
    setLocalValue(null);
    setValidationError(null);

    const updatedInstanceDeferred = [
      ...instanceDeferredQitIds,
      question.qit_id,
    ];
    const updatedFlatDeferred = [...flatDeferredQitIds, question.qit_id];
    const remaining = computeVisibleQuestionQueue(
      currentGroup.questions,
      localAnswers,
      updatedInstanceDeferred,
      pendingFileQits.map((p) => p.qit_id),
      onlyQitIds
    );

    if (remaining.length === 0) {
      let nextAgMap3 = answerGroupQits;
      if (answerGroupQits && activeAgTarget != null) {
        nextAgMap3 = { ...answerGroupQits };
        delete nextAgMap3[activeAgTarget];
      }
      if (
        currentGroup?.group_type === 'multi' &&
        advanceToNextAnswerGroup(nextAgMap3)
      ) {
        return;
      }
      if (currentGroup?.group_type === 'multi' && !onlyQitIds) {
        // Clear the answer-group auto-advance state so the AddAnotherScreen's
        // "No, I'm done" callIPE does not re-send a stale answer_group_qits
        // (which would re-select this multi group instead of advancing past it).
        if (answerGroupQits) {
          dispatch(interviewActions.clearAnswerGroupState());
        }
        dispatch(interviewActions.setWorkItemType('add_another'));
      } else {
        const nextOnlyQitIds = onlyQitIds
          ? onlyQitIds.filter((id) => !updatedFlatDeferred.includes(id))
          : undefined;
        // All targeted QITs skipped — return to the caller.
        // Unless there are more answer_groups to process.
        if (onlyQitIds && !nextOnlyQitIds?.length) {
          if (answerGroupQits && Object.keys(answerGroupQits).length === 0) {
            dispatch(interviewActions.clearAnswerGroupState());
            dispatch(interviewActions.setWorkItemType('add_another'));
            return;
          }
          navigate(backPath ?? '/renter');
          return;
        }
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            `[EXH] → CALL_IPE (skip)  nextOnly=${_only(
              nextOnlyQitIds ?? undefined
            )}`
          );
        }
        dispatch(interviewActions.setLoading(true));
        callIPE({
          skippedQitIds: updatedFlatDeferred,
          onlyQitIds: nextOnlyQitIds,
        });
      }
    }
  };

  const showBack = cursor > 0;

  return (
    <Box
      data-testid="question-screen"
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      <Box
        id="interview-body"
        sx={{
          flex: { xs: 1, md: '0 0 auto' },
          overflow: 'auto',
          bgcolor: { md: 'background.neutral' },
          display: 'flex',
          alignItems: { md: 'flex-start' },
          justifyContent: { md: 'center' },
          pt: { md: 4 },
          gap: { md: 3 },
        }}
      >
        <ContentCard key={currentQitId} sx={fadeStyle}>
          <Box>
            <SectionLabel label={currentGroup.section_name || ''} />
            {currentGroup.group_name &&
              currentGroup.group_name !== currentGroup.section_name && (
                <Typography
                  variant="subtitle2"
                  sx={{ color: 'text.secondary' }}
                >
                  {currentGroup.group_name}
                </Typography>
              )}
          </Box>
          <Box>
            <InstanceBadge label={currentGroup?.instance_label ?? null} />
            <QuestionText
              text={question.question_text}
              required={question.necessity === 1}
              key={`q${question.qit_id}` || 0}
            />
            <HelpText text={question.help_text} />
            <FileUploadNotice
              show={
                question.options?.some((o) => o.requires_file_upload) ?? false
              }
            />
          </Box>
          <AIProvenanceHint
            show={question.always_ask && question.ai_extracted}
          />
          <ValidationError message={validationError ?? answerError} />
          <QuestionInput
            question={question}
            value={localValue ?? question.existing_answer}
            onChange={handleChange}
            surveyId={surveyId as number}
            answerGroupId={currentAnswerGroupId}
            questionGroupId={currentGroup.group_id}
            onAnswerGroupResolved={handleAnswerGroupResolved}
            certificationId={certId ?? undefined}
            onComplete={handleFileComplete}
            onReadyStateChange={setFileReadyState}
            onContinueTrigger={handleContinueTrigger}
            instanceKey={`s${instanceSeqByGroup[currentGroup.group_id] ?? 0}`}
            skipInitialFileFetch={
              question.question_type === 'file' &&
              currentGroup.group_type === 'multi' &&
              currentAnswerGroupId == null
            }
            existingSurveyFiles={
              question.question_type === 'file' &&
              Array.isArray(question.existing_answer)
                ? question.existing_answer
                : undefined
            }
          />
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            {showBack && (
              <Button
                onClick={handleBack}
                disabled={answerInFlight || loading}
                data-testid="back-button-inline"
                sx={{
                  height: 44,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  color: 'text.secondary',
                  fontSize: 14,
                  fontWeight: 500,
                  px: 2.5,
                  textTransform: 'none',
                }}
              >
                Back
              </Button>
            )}
            {!onlyQitIds && (
              <Button
                onClick={handleSkip}
                disabled={answerInFlight || loading}
                data-testid="skip-button-inline"
                sx={{
                  height: 44,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  color: 'text.secondary',
                  fontSize: 14,
                  fontWeight: 500,
                  px: 2.5,
                  textTransform: 'none',
                }}
              >
                Skip for now
              </Button>
            )}
            {question.question_type !== 'file' && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveAndNext}
                disabled={answerInFlight || loading || !!validationError}
                data-testid="save-next-button-inline"
                startIcon={
                  answerInFlight ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
                sx={{
                  height: 44,
                  borderRadius: '12px',
                  fontSize: 16,
                  fontWeight: 600,
                  px: 3,
                  textTransform: 'none',
                }}
              >
                <span>Save &amp; Next Question</span>
              </Button>
            )}
            {question.question_type === 'file' && fileReadyState.ready && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => continueTriggerRef.current?.()}
                disabled={!fileReadyState.ready}
                data-testid="file-continue-button-inline"
                startIcon={
                  fileReadyState.processing ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
                sx={{
                  height: 44,
                  borderRadius: '12px',
                  fontSize: 16,
                  fontWeight: 600,
                  px: 3,
                  textTransform: 'none',
                }}
              >
                <span>
                  {fileReadyState.processing
                    ? 'Processing files...'
                    : 'Continue'}
                </span>
              </Button>
            )}
          </Box>
        </ContentCard>
      </Box>

      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {question.question_type === 'file' ? (
          fileReadyState.ready ? (
            <InterviewFooter
              primaryLabel={
                fileReadyState.processing ? 'Processing files...' : 'Continue'
              }
              primaryDisabled={!fileReadyState.ready}
              onPrimary={() => continueTriggerRef.current?.()}
              primaryStartIcon={
                fileReadyState.processing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              primaryTestId="file-continue-button"
              secondaryLabel="Skip for now"
              onSecondary={handleSkip}
              secondaryDisabled={answerInFlight || loading}
              secondaryTestId="skip-button"
              tertiaryLabel={showBack ? 'Back' : undefined}
              onTertiary={showBack ? handleBack : undefined}
              tertiaryDisabled={answerInFlight || loading}
              tertiaryTestId="back-button"
            />
          ) : showBack ? (
            <InterviewFooter
              secondaryLabel="Skip for now"
              onSecondary={handleSkip}
              secondaryDisabled={answerInFlight || loading}
              secondaryTestId="skip-button"
              tertiaryLabel="Back"
              onTertiary={handleBack}
              tertiaryDisabled={answerInFlight || loading}
              tertiaryTestId="back-button"
            />
          ) : (
            <InterviewFooter
              secondaryLabel="Skip for now"
              onSecondary={handleSkip}
              secondaryDisabled={answerInFlight || loading}
              secondaryTestId="skip-button"
            />
          )
        ) : showBack ? (
          <InterviewFooter
            primaryLabel="Save & Next Question"
            secondaryLabel={onlyQitIds ? undefined : 'Skip for now'}
            onPrimary={handleSaveAndNext}
            onSecondary={onlyQitIds ? undefined : handleSkip}
            primaryDisabled={answerInFlight || loading || !!validationError}
            primaryStartIcon={
              answerInFlight ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            secondaryDisabled={answerInFlight || loading}
            primaryTestId="save-next-button"
            secondaryTestId="skip-button"
            tertiaryLabel="Back"
            onTertiary={handleBack}
            tertiaryDisabled={answerInFlight || loading}
            tertiaryTestId="back-button"
          />
        ) : (
          <InterviewFooter
            primaryLabel="Save & Next Question"
            secondaryLabel={onlyQitIds ? undefined : 'Skip for now'}
            onPrimary={handleSaveAndNext}
            onSecondary={onlyQitIds ? undefined : handleSkip}
            primaryDisabled={answerInFlight || loading || !!validationError}
            primaryStartIcon={
              answerInFlight ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            secondaryDisabled={answerInFlight || loading}
            primaryTestId="save-next-button"
            secondaryTestId="skip-button"
          />
        )}
      </Box>
    </Box>
  );
}
